import React, { useMemo } from 'react';
import { LightTable, LightTableColumDef } from '../../../theme/components';
import {
  ItemsWorkspaceRequestWrapped,
  getWorkspaceQuotaUsageWithChildrenSelector,
} from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import { getPolyglot } from '../../../i18n';
import moment from 'moment';
import {
  getTextStatusFromRequest,
  getTypeStatusFromRequest,
} from '../util/getStatusFromRequest';
import DetailsIconButton from '../../../theme/icons/DetailsIconButton';
import { Link as LinkRouter } from 'react-router-dom';
import { LabelState } from '../../../theme/components';
import { useAppSelector } from '../../../redux/store.model';
import { getUserSelector } from '../../../redux/users/api/User.selectors';
import { arrayToRecord } from '../../../util/ArrayUtil';
import { IUser } from '../../../redux/users/api/user.model';
import { isNotNullOrUndefined } from '../../../util/types/Array';
import { Box, Link, Skeleton, styled } from '@mui/material';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';

export interface CustomersTableProps {
  items: ItemsWorkspaceRequestWrapped[];
}

/**
/**
 * Determine if a workspace is a Supplier (Organization) based on child count.
 * Returns 'organization' | 'simple' | undefined
 */
export function getWorkspaceType(
  childCount: number | undefined
): 'organization' | 'simple' | undefined {
  if (childCount === undefined) return undefined;
  return childCount > 0 ? 'organization' : 'simple';
}

function CustomersTable(props: CustomersTableProps) {
  const { items } = props;
  const polyglot = getPolyglot();
  const usersDicSerialized = useAppSelector((state) =>
    JSON.stringify(
      arrayToRecord<IUser, IUser>(
        items
          .map((request) => {
            const user =
              request.workspace?.accountHolderId ||
              request.lastRequest?.creatorId;
            return user
              ? (getUserSelector(user)(state).data as IUser)
              : undefined;
          })
          .filter(isNotNullOrUndefined),
        'id'
      )
    )
  );
  const usersDic: Record<string, IUser> = useMemo(
    () => JSON.parse(usersDicSerialized),
    [usersDicSerialized]
  );

  // Build child count per workspace using childIds when available, fallback to descendants
  const childCountDicSerialized = useAppSelector((state) => {
    const dic: Record<string, number | undefined> = {};
    items.forEach((request) => {
      const workspace = request.workspace;
      if (!workspace?.id) return;
      const childIds = workspace.childIds;
      if (childIds !== null && childIds !== undefined) {
        dic[workspace.id] = childIds.length;
      } else {
        const descendants =
          StoerkIdAPI.endpoints.getWorkspaceDescendants.select(workspace.id)(
            state
          ).data;
        dic[workspace.id] = descendants ? descendants.length : undefined;
      }
    });
    return JSON.stringify(dic);
  });
  const childCountDic: Record<string, number | undefined> = useMemo(
    () => JSON.parse(childCountDicSerialized),
    [childCountDicSerialized]
  );

  // Compute total usage including child workspaces (includeChildWorkspaces=true)
  // Uses shallowEqual via JSON serialization to prevent unnecessary re-renders
  const totalUsageSerialized = useAppSelector((state) => {
    const dic: Record<string, { commanders: number; users: number }> = {};
    items.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (!workspaceId) return;
      const usage =
        getWorkspaceQuotaUsageWithChildrenSelector(workspaceId)(state).data;
      const commanders = usage?.commanders?.length ?? 0;
      const users = usage?.users?.length ?? 0;
      dic[workspaceId] = { commanders, users };
    });
    return JSON.stringify(dic);
  });
  const totalUsageDic: Record<string, { commanders: number; users: number }> =
    useMemo(() => JSON.parse(totalUsageSerialized), [totalUsageSerialized]);

  // Track which workspaces still have usage loading
  const loadingUsageSetSerialized = useAppSelector((state) => {
    const loading: string[] = [];
    items.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (!workspaceId) return;
      const result =
        getWorkspaceQuotaUsageWithChildrenSelector(workspaceId)(state);
      if (result.isLoading || result.isUninitialized) {
        loading.push(workspaceId);
      }
    });
    return JSON.stringify(loading);
  });
  const loadingUsageSet: Set<string> = useMemo(
    () => new Set(JSON.parse(loadingUsageSetSerialized)),
    [loadingUsageSetSerialized]
  );

  // Determine if the stores column should be shown
  // (any workspace has subworkspaces or flexeserveUI.hierarchy setting)
  const showStoresColumn = useAppSelector((state) => {
    return items.some((request) => {
      const workspace = request.workspace;
      if (!workspace?.id) return false;
      const childIds = workspace.childIds;
      const hasChildren =
        childIds !== null && childIds !== undefined
          ? childIds.length > 0
          : (
              StoerkIdAPI.endpoints.getWorkspaceDescendants.select(
                workspace.id
              )(state).data ?? []
            ).length > 0;
      const hasHierarchySetting =
        workspace?.settings?.['flexeserveUI.hierarchy'];
      return hasChildren || hasHierarchySetting;
    });
  });

  // Fetch stores usage data
  const storesUsageSerialized = useAppSelector((state) => {
    const dic: Record<string, number> = {};
    items.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (!workspaceId) return;
      const result = StoerkIdAPI.endpoints.getWorkspaceStoresUsage.select({
        workspaceId,
        includeChildWorkspaces: true,
        includeStores: false,
      })(state);
      if (result.data) {
        dic[workspaceId] = result.data.totalStoresCount;
      }
    });
    return JSON.stringify(dic);
  });
  const storesUsageDic: Record<string, number> = useMemo(
    () => JSON.parse(storesUsageSerialized),
    [storesUsageSerialized]
  );

  // Track loading state for stores usage
  const loadingStoresSetSerialized = useAppSelector((state) => {
    const loading: string[] = [];
    items.forEach((request) => {
      const workspaceId = request.workspace?.id;
      if (!workspaceId) return;
      const result = StoerkIdAPI.endpoints.getWorkspaceStoresUsage.select({
        workspaceId,
        includeChildWorkspaces: true,
        includeStores: false,
      })(state);
      if (result.isLoading) {
        loading.push(workspaceId);
      }
    });
    return JSON.stringify(loading);
  });
  const loadingStoresSet: Set<string> = useMemo(
    () => new Set(JSON.parse(loadingStoresSetSerialized)),
    [loadingStoresSetSerialized]
  );

  const columns: LightTableColumDef[] = useMemo(
    () => [
      // {
      //   field: 'id',
      //   headerName: 'Reference',
      //   orderable: true,
      // },
      {
        field: 'workspaceName',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_workspace_name'
        ),
        orderable: true,
      },
      {
        field: 'type',
        headerName: polyglot.t('stoerk_id.customer_list.header_cell_type'),
        orderable: true,
      },
      {
        field: 'email',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_billing_email'
        ),
        orderable: true,
        TableCellProps: {
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 220,
          },
        },
      },
      {
        field: 'stripeId',
        headerName: polyglot.t(
          'stoerk_id.detail_page.status.stripe_id_header_cell'
        ),
        orderable: true,
      },
      {
        field: 'lastRequestDate',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_last_request_date'
        ),
        orderable: true,
      },
      {
        field: 'lastRequestStatus',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_last_request_status'
        ),
        orderable: true,
      },
      {
        field: 'userQuota',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_billing_users'
        ),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      {
        field: 'deviceQuota',
        headerName: polyglot.t(
          'stoerk_id.customer_list.header_cell_billing_commanders'
        ),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      ...(showStoresColumn
        ? [
            {
              field: 'stores',
              headerName: polyglot.t(
                'stoerk_id.customer_list.header_cell_stores'
              ),
              orderable: true,
              TableCellProps: { align: 'right' as const },
            },
          ]
        : []),

      {
        field: 'detail',
        headerName: polyglot.t('stoerk_id.customer_list.header_cell_customer'),
        TableCellProps: { align: 'center', width: '80px' },
      },
    ],
    [polyglot, showStoresColumn]
  );

  const rows = useMemo(
    () =>
      items.map((item, i) => {
        const name = item.lastRequest?.workspaceName ?? item.workspace?.name;
        const url = item.workspace?.id
          ? `/account/id/${item.workspace.id}`
          : `/account/user/${item.creatorId}`;
        const email =
          usersDic[item.workspace?.accountHolderId as any]?.email ??
          usersDic[item.creatorId as any]?.email ??
          item.billingEmail ??
          item.workspace?.accountHolderId ??
          '';

        const workspaceType = getWorkspaceType(
          childCountDic[item.workspace?.id ?? '']
        );

        return {
          // use as key
          id: `${item.id}-${i}`,
          workspaceName: {
            value: name,
            element:
              item.creatorId || item.workspace?.id ? (
                <Link to={url} component={LinkRouter}>
                  {name}
                </Link>
              ) : (
                name
              ),
          },
          type: {
            value: workspaceType ?? '',
            element: workspaceType
              ? polyglot.t(`stoerk_id.customer_list.type_${workspaceType}`)
              : '',
          },
          stripeId: (item.workspace as any)?.stripeId ?? item.stripeUserId,
          email:
            usersDic[item.workspace?.accountHolderId as any]?.email ??
            usersDic[item.creatorId as any]?.email ??
            item.billingEmail ??
            item.workspace?.accountHolderId ??
            '',
          lastRequestDate: {
            value: item.lastRequest?.created ?? -Infinity,
            element: item.lastRequest?.created
              ? moment(item.lastRequest?.created).format('L')
              : '',
          },
          userQuota: {
            value: totalUsageDic[item?.workspace?.id ?? '']?.users ?? 0,
            element: loadingUsageSet.has(item?.workspace?.id ?? '') ? (
              <Skeleton width={40} sx={{ ml: 'auto' }} />
            ) : (
              totalUsageDic[item?.workspace?.id ?? '']?.users ?? ''
            ),
          },
          deviceQuota: {
            value: totalUsageDic[item?.workspace?.id ?? '']?.commanders ?? 0,
            element: loadingUsageSet.has(item?.workspace?.id ?? '') ? (
              <Skeleton width={40} sx={{ ml: 'auto' }} />
            ) : (
              totalUsageDic[item?.workspace?.id ?? '']?.commanders ?? ''
            ),
          },
          lastRequestStatus: {
            value: item.requestStatus,
            element: (
              <LabelState
                variant={
                  item.lastRequest
                    ? getTypeStatusFromRequest(item.lastRequest)
                    : 'info'
                }
              >
                {item.lastRequest
                  ? polyglot.t(getTextStatusFromRequest(item.lastRequest))
                  : polyglot.t('stoerk_id.detail_page.request.no_request')}
              </LabelState>
            ),
          },
          ...(showStoresColumn
            ? {
                stores: {
                  value: storesUsageDic[item?.workspace?.id ?? ''] ?? 0,
                  element: loadingStoresSet.has(item?.workspace?.id ?? '') ? (
                    <Skeleton width={40} sx={{ ml: 'auto' }} />
                  ) : (
                    storesUsageDic[item?.workspace?.id ?? ''] ?? ''
                  ),
                },
              }
            : {}),
          detail:
            item.creatorId || item.workspace?.id ? (
              <LinkRouter to={url}>
                <DetailsIconButton />
              </LinkRouter>
            ) : null,
        };
      }),
    [
      items,
      usersDic,
      childCountDic,
      totalUsageDic,
      loadingUsageSet,
      showStoresColumn,
      storesUsageDic,
      loadingStoresSet,
      polyglot,
    ]
  );

  return (
    <LightTable
      columns={columns}
      rows={rows}
      variant="clear"
      TableContainerComponent={TableContainerComponent}
      orderByDefault="lastRequestDate"
    />
  );
}

const TableContainerComponent = styled(Box)(({ theme }) => ({}));
export default CustomersTable;
